import React, { useRef, useEffect, useState } from 'react';
import './StatsSection.css';
import { FaUniversity, FaGraduationCap, FaBuilding, FaChartLine, FaBook } from 'react-icons/fa';
import { motion, useInView, useMotionValue, useSpring } from 'framer-motion';
import ApiService from '../../../services/apiService';

// Animated Counter Component
const AnimatedCounter = ({ value, duration = 2 }) => {
  const ref = useRef(null);
  const motionValue = useMotionValue(0);
  const springValue = useSpring(motionValue, { duration: duration * 1000 });
  const isInView = useInView(ref, { once: false, margin: "-100px" });

  const [displayValue, setDisplayValue] = useState("0");

  useEffect(() => {
    if (isInView) {
      // Reset to 0 first, then animate to target value
      motionValue.set(0);
      setTimeout(() => {
        const numericValue = parseFloat(value.replace(/[^\d.]/g, ''));
        motionValue.set(numericValue);
      }, 100);
    } else {
      // Reset to 0 when out of view
      motionValue.set(0);
    }
  }, [motionValue, isInView, value]);

  useEffect(() => {
    springValue.on("change", (latest) => {
      // Format the number based on original value format
      if (value.includes('₹')) {
        setDisplayValue(`₹${Math.round(latest * 10) / 10}Cr`);
      } else if (value.includes('+')) {
        setDisplayValue(`${Math.round(latest).toLocaleString()}+`);
      } else {
        setDisplayValue(Math.round(latest).toString());
      }
    });
  }, [springValue, value]);

  return <span ref={ref}>{displayValue}</span>;
};

const StatsSection = () => {
  const [statsData, setStatsData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Default static structure for fallback (keeping establishment year static)
  const defaultStatsStructure = [
    {
      id: 'stats-establishment',
      title: 'Year of Establishment',
      value: '2008',
      icon: FaUniversity,
      description: 'Years of Excellence'
    },
    {
      id: 'stats-constituent',
      title: 'Constituent Colleges',
      value: '0',
      icon: FaGraduationCap,
      description: 'Academic Institutions'
    },
    {
      id: 'stats-affiliated',
      title: 'Affiliated Colleges',
      value: '0',
      icon: FaBuilding,
      description: 'Partner Institutions'
    },
    {
      id: 'stats-grants',
      title: 'Research Grants',
      value: '₹0Cr',
      icon: FaChartLine,
      description: 'Funding Secured'
    },
    {
      id: 'stats-publications',
      title: 'Publicationssss',
      value: '0+',
      icon: FaBook,
      description: 'Research Papers'
    }
  ];

  useEffect(() => {
    const fetchStats = async () => {
      try {
        setLoading(true);
        console.log('🔄 Loading stats from WordPress API...');
        
        const response = await ApiService.getStats();
        console.log('📥 API Response received:', response);
        
        if (response.data && response.data.length > 0) {
          // Use the latest stats entry (first one in the array)
          const latestStats = response.data[0];
          
          // Transform WordPress API response to match our component structure
          const transformedData = [
            {
              id: 'stats-establishment',
              title: 'Year of Establishment',
              value: '2008', // Keep this static
              icon: FaUniversity,
              description: 'Years of Excellence'
            },
            {
              id: 'stats-constituent',
              title: 'Constituent Colleges',
              value: latestStats.acf?.constituent_colleges || '0',
              icon: FaGraduationCap,
              description: 'Academic Institutions'
            },
            {
              id: 'stats-affiliated',
              title: 'Affiliated Colleges',
              value: latestStats.acf?.affiliated_colleges || '0',
              icon: FaBuilding,
              description: 'Partner Institutions'
            },
            {
              id: 'stats-grants',
              title: 'Research Grants',
              value: `₹${latestStats.acf?.research_grants || '0'}Cr`,
              icon: FaChartLine,
              description: 'Funding Secured'
            },
            {
              id: 'stats-publications',
              title: 'Publications',
              value: `${latestStats.acf?.publications || '0'}+`,
              icon: FaBook,
              description: 'Research Papers'
            }
          ];
          
          console.log('✅ Using WordPress API stats data:', transformedData);
          console.log('📊 Latest stats entry ID:', latestStats.id);
          
          setStatsData(transformedData);
          setError(null);
        } else {
          console.log('⚠️  No stats from API, using default structure');
          setStatsData(defaultStatsStructure);
          setError(null);
        }
      } catch (err) {
        console.error('❌ Error loading stats from WordPress API:', err);
        setStatsData(defaultStatsStructure);
        setError('Failed to load stats');
      } finally {
        setLoading(false);
        console.log('🏁 Stats loading completed');
      }
    };

    fetchStats();
  }, []);

  return (
    <section id="rayalseema-stats-section" className="rayalseema-stats-section">
      <div className="rayalseema-stats-container">
        
        <div className="rayalseema-stats-grid">
          {loading ? (
            // Loading state
            [...Array(5)].map((_, index) => (
              <div 
                key={`loading-${index}`}
                className={`rayalseema-stat-card rayalseema-stat-card-${index + 1}`}
                style={{ opacity: 0.7 }}
              >
                <div className="rayalseema-stat-icon">
                  <div className="rayalseema-stat-react-icon" style={{ 
                    width: '40px', 
                    height: '40px', 
                    backgroundColor: '#f0f0f0', 
                    borderRadius: '50%' 
                  }}></div>
                </div>
                <div className="rayalseema-stat-content">
                  <h3 className="rayalseema-stat-value">Loading...</h3>
                  <h4 className="rayalseema-stat-title">Loading...</h4>
                  <p className="rayalseema-stat-description">Loading data...</p>
                </div>
                <div className="rayalseema-stat-overlay"></div>
              </div>
            ))
          ) : (
            statsData.map((stat, index) => {
              const IconComponent = stat.icon;
              return (
                <div 
                  key={stat.id} 
                  className={`rayalseema-stat-card rayalseema-stat-card-${index + 1}`}
                >
                  <div className="rayalseema-stat-icon">
                    <IconComponent className="rayalseema-stat-react-icon" />
                  </div>
                  <div className="rayalseema-stat-content">
                    <h3 className="rayalseema-stat-value">
                      <AnimatedCounter value={stat.value} duration={2} />
                    </h3>
                    <h4 className="rayalseema-stat-title">{stat.title}</h4>
                    <p className="rayalseema-stat-description">{stat.description}</p>
                  </div>
                  <div className="rayalseema-stat-overlay"></div>
                </div>
              );
            })
          )}
        </div>
      </div>
    </section>
  );
};

export default StatsSection;